import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { useToast } from '../context/ToastContext';

interface RuleConfigProps {
  token: string;
  collectionId: string;
  onChange: (rules: any[]) => void;
}

const Tooltip = ({ text }: { text: string }) => (
  <div className="group relative flex items-center ml-1">
    <svg tabIndex={0} role="tooltip" aria-label={text} className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 focus:outline-none focus:text-slate-600 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block group-focus-within:block w-48 p-2 bg-slate-800 text-white text-xs rounded shadow-lg z-10 text-center whitespace-normal pointer-events-none">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
    </div>
  </div>
);

export default function RuleConfig({ token, collectionId, onChange }: RuleConfigProps) {
  const { showToast } = useToast();
  const [fields, setFields] = useState<any[]>([]);
  const [rulesState, setRulesState] = useState<Record<string, Record<string, any>>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      try {
        const [fieldsRes, rulesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/inspect/collection/${collectionId}/fields`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_BASE_URL}/api/config/rules/${collectionId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        
        const fieldsData = await fieldsRes.json();
        const rulesData = await rulesRes.json();
        
        setFields(fieldsData.fields || []);
        
        const newRulesState: Record<string, Record<string, any>> = {};
        if (rulesData.rules) {
          rulesData.rules.forEach((r: any) => {
            if (!newRulesState[r.fieldSlug]) {
              newRulesState[r.fieldSlug] = {};
            }
            newRulesState[r.fieldSlug][r.ruleType] = r;
          });
        }
        setRulesState(newRulesState);
        onChange(Object.values(newRulesState).flatMap(r => Object.values(r)));
      } catch (err) {
        console.error('Failed to load fields and rules', err);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, [collectionId, token]);

  const handleRuleChange = (fieldSlug: string, ruleType: string, ruleValue: any) => {
    const newRulesState = { ...rulesState };
    if (!newRulesState[fieldSlug]) {
      newRulesState[fieldSlug] = {};
    }
    
    if (ruleValue === false || ruleValue === '' || ruleValue === null) {
      delete newRulesState[fieldSlug][ruleType];
      if (Object.keys(newRulesState[fieldSlug]).length === 0) {
        delete newRulesState[fieldSlug];
      }
    } else {
      newRulesState[fieldSlug][ruleType] = { fieldSlug, ruleType, ruleValue: String(ruleValue) };
    }
    
    setRulesState(newRulesState);
    const flatRules = Object.values(newRulesState).flatMap(fieldRules => Object.values(fieldRules));
    onChange(flatRules);
  };

  const handleDeleteRule = async (fieldSlug: string, ruleType: string) => {
    try {
      const url = new URL(`${API_BASE_URL}/api/config/rule`);
      url.searchParams.append('collectionId', collectionId);
      url.searchParams.append('fieldSlug', fieldSlug);
      url.searchParams.append('ruleType', ruleType);

      const res = await fetch(url.toString(), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Delete failed');

      const newRulesState = { ...rulesState };
      if (newRulesState[fieldSlug]) {
        delete newRulesState[fieldSlug][ruleType];
        if (Object.keys(newRulesState[fieldSlug]).length === 0) {
          delete newRulesState[fieldSlug];
        }
      }
      setRulesState(newRulesState);
      const flatRules = Object.values(newRulesState).flatMap(fieldRules => Object.values(fieldRules));
      onChange(flatRules);
      showToast('Rule deleted', 'success');
    } catch (err) {
      console.error('Failed to delete rule', err);
      showToast('Failed to delete rule', 'error');
    }
  };

  const renderDeleteButton = (fieldSlug: string, ruleType: string) => {
    if (!rulesState[fieldSlug]?.[ruleType]) return null;
    return (
      <button 
        aria-label={`Delete ${ruleType} rule`}
        onClick={() => handleDeleteRule(fieldSlug, ruleType)}
        className="p-1 text-[var(--text3)] hover:text-red-400 hover:bg-[var(--background3)] rounded transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    );
  };

  if (isLoading) {
    return <div className="bg-[#2b2b2b] p-4 rounded-[3px] border border-[var(--border1)] text-[12px] md:text-[13px] text-[var(--textInactive)]">Loading fields and rules...</div>;
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-1 mb-4">
        <h3 className="text-[14px] md:text-[15px] font-medium text-[var(--text1)]">Inspection Rules</h3>
        <p className="text-[11px] md:text-[12px] text-[var(--text3)]">Configure rules for specific fields.</p>
      </div>
      
      <div className="space-y-3">


        {fields.map(field => {
          if (field.type === 'PlainText') {
            return (
              <div key={field.slug} className="p-3 bg-[var(--background2)] rounded-[3px] border border-[var(--border1)] space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[var(--text1)] text-[12px] md:text-[13px]">{field.displayName}</p>
                    <p className="text-[10px] md:text-[11px] text-[var(--text3)]">Plain Text</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 pl-2 border-l border-[#186ade]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <label htmlFor={`max-length-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Max Characters</label>
                      <Tooltip text="Flags an error if the text exceeds this specific character limit." />
                    </div>
                    <div className="flex items-center gap-1">
                      <input 
                        id={`max-length-${field.slug}`}
                        type="number" 
                        placeholder="No limit"
                        value={rulesState[field.slug]?.['MAX_TEXT_LENGTH']?.ruleValue || ''}
                        onChange={(e) => handleRuleChange(field.slug, 'MAX_TEXT_LENGTH', e.target.value)}
                        className="bg-[var(--background3)] border border-[var(--border2)] rounded-[2px] px-2 py-1 w-20 text-[11px] md:text-[12px] text-[var(--text1)]"
                      />
                      {renderDeleteButton(field.slug, 'MAX_TEXT_LENGTH')}
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-1.5 w-full">
                    <div className="flex items-center">
                      <label htmlFor={`banned-words-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Forbidden Words</label>
                      <Tooltip text="Flags an error if any of these comma-separated words are found in the text." />
                    </div>
                    <div className="flex items-center gap-1 w-full">
                      <input 
                        id={`banned-words-${field.slug}`}
                        type="text" 
                        placeholder="foo, bar"
                        value={rulesState[field.slug]?.['BANNED_WORDS']?.ruleValue || ''}
                        onChange={(e) => handleRuleChange(field.slug, 'BANNED_WORDS', e.target.value)}
                        className="bg-[var(--background3)] border border-[var(--border2)] rounded-[2px] px-2 py-1 w-full text-[11px] md:text-[12px] text-[var(--text1)]"
                      />
                      {renderDeleteButton(field.slug, 'BANNED_WORDS')}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center">
                      <label htmlFor={`trailing-spaces-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Check Trailing Spaces</label>
                      <Tooltip text="Flags an error if the text begins or ends with whitespace." />
                    </div>
                    <div className="flex items-center gap-1">
                      <input 
                        id={`trailing-spaces-${field.slug}`}
                        type="checkbox" 
                        checked={rulesState[field.slug]?.['TRAILING_SPACES']?.ruleValue === 'true'}
                        onChange={(e) => handleRuleChange(field.slug, 'TRAILING_SPACES', e.target.checked)}
                        className="h-3.5 w-3.5 text-[var(--blueText)] rounded-[2px] bg-[var(--background3)] border-[var(--border2)]" 
                      />
                      {renderDeleteButton(field.slug, 'TRAILING_SPACES')}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center">
                      <label htmlFor={`text-casing-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Text Casing</label>
                      <Tooltip text="Flags an error if the text does not match the selected casing." />
                    </div>
                    <div className="flex items-center gap-1">
                      <select 
                        id={`text-casing-${field.slug}`}
                        aria-label="Text Casing"
                        value={rulesState[field.slug]?.['TEXT_CASING']?.ruleValue || ''}
                        onChange={(e) => handleRuleChange(field.slug, 'TEXT_CASING', e.target.value)}
                        className="bg-[var(--background3)] border border-[var(--border2)] rounded-[2px] px-2 py-1 text-[11px] md:text-[12px] text-[var(--text1)]"
                      >
                        <option value="">Any</option>
                        <option value="LOWERCASE">lowercase</option>
                        <option value="UPPERCASE">UPPERCASE</option>
                      </select>
                      {renderDeleteButton(field.slug, 'TEXT_CASING')}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          
          if (field.type === 'Image') {
            return (
              <div key={field.slug} className="p-3 bg-[var(--background2)] rounded-[3px] border border-[var(--border1)] space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[var(--text1)] text-[12px] md:text-[13px]">{field.displayName}</p>
                    <p className="text-[10px] md:text-[11px] text-[var(--text3)]">Image</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 pl-2 border-l border-[#186ade]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <label htmlFor={`max-image-size-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Max File Size</label>
                      <Tooltip text="Flags an error if the image file size is larger than the selected limit." />
                    </div>
                    <div className="flex items-center gap-1">
                      <select 
                        id={`max-image-size-${field.slug}`}
                        aria-label="Max File Size"
                        value={rulesState[field.slug]?.['MAX_IMAGE_SIZE']?.ruleValue || ''}
                        onChange={(e) => handleRuleChange(field.slug, 'MAX_IMAGE_SIZE', e.target.value)}
                        className="bg-[var(--background3)] border border-[var(--border2)] rounded-[2px] px-2 py-1 text-[11px] md:text-[12px] text-[var(--text1)]"
                      >
                        <option value="">No limit</option>
                        <option value="512000">500 KB</option>
                        <option value="1048576">1 MB</option>
                        <option value="2097152">2 MB</option>
                      </select>
                      {renderDeleteButton(field.slug, 'MAX_IMAGE_SIZE')}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <label htmlFor={`missing-alt-text-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Check Missing Alt Text</label>
                      <Tooltip text="Flags an error if this standard image field is missing descriptive alt text." />
                    </div>
                    <div className="flex items-center gap-1">
                      <input 
                        id={`missing-alt-text-${field.slug}`}
                        type="checkbox" 
                        checked={rulesState[field.slug]?.['MISSING_ALT_TEXT']?.ruleValue === 'true'}
                        onChange={(e) => handleRuleChange(field.slug, 'MISSING_ALT_TEXT', e.target.checked)}
                        className="h-3.5 w-3.5 text-[var(--blueText)] rounded-[2px] bg-[var(--background3)] border-[var(--border2)]" 
                      />
                      {renderDeleteButton(field.slug, 'MISSING_ALT_TEXT')}
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-1.5 w-full pt-1">
                    <div className="flex items-center">
                      <label htmlFor={`allowed-formats-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Allowed Formats</label>
                      <Tooltip text="Flags an error if the image format is not in this comma-separated list (e.g., webp, avif)." />
                    </div>
                    <div className="flex items-center gap-1 w-full">
                      <input 
                        id={`allowed-formats-${field.slug}`}
                        type="text" 
                        placeholder="webp, png, jpg"
                        value={rulesState[field.slug]?.['ALLOWED_IMAGE_FORMATS']?.ruleValue || ''}
                        onChange={(e) => handleRuleChange(field.slug, 'ALLOWED_IMAGE_FORMATS', e.target.value)}
                        className="bg-[var(--background3)] border border-[var(--border2)] rounded-[2px] px-2 py-1 w-full text-[11px] md:text-[12px] text-[var(--text1)]"
                      />
                      {renderDeleteButton(field.slug, 'ALLOWED_IMAGE_FORMATS')}
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          if (field.type === 'RichText') {
            return (
              <div key={field.slug} className="p-3 bg-[var(--background2)] rounded-[3px] border border-[var(--border1)] space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[var(--text1)] text-[12px] md:text-[13px]">{field.displayName}</p>
                    <p className="text-[10px] md:text-[11px] text-[var(--text3)]">Rich Text</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 pl-2 border-l border-[#186ade]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <label htmlFor={`heading-hierarchy-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Check Heading Hierarchy</label>
                      <Tooltip text="Flags an error if heading tags are skipped (e.g., jumping directly from H1 to H3)." />
                    </div>
                    <div className="flex items-center gap-1">
                      <input 
                        id={`heading-hierarchy-${field.slug}`}
                        type="checkbox" 
                        checked={rulesState[field.slug]?.['HEADING_HIERARCHY']?.ruleValue === 'true'}
                        onChange={(e) => handleRuleChange(field.slug, 'HEADING_HIERARCHY', e.target.checked)}
                        className="h-3.5 w-3.5 text-[var(--blueText)] rounded-[2px] bg-[var(--background3)] border-[var(--border2)]" 
                      />
                      {renderDeleteButton(field.slug, 'HEADING_HIERARCHY')}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <label htmlFor={`broken-links-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Check Broken Links</label>
                      <Tooltip text="Visits all URLs inside the rich text and flags any that return a 404 (Not Found)." />
                    </div>
                    <div className="flex items-center gap-1">
                      <input 
                        id={`broken-links-${field.slug}`}
                        type="checkbox" 
                        checked={rulesState[field.slug]?.['BROKEN_LINKS']?.ruleValue === 'true'}
                        onChange={(e) => handleRuleChange(field.slug, 'BROKEN_LINKS', e.target.checked)}
                        className="h-3.5 w-3.5 text-[var(--blueText)] rounded-[2px] bg-[var(--background3)] border-[var(--border2)]" 
                      />
                      {renderDeleteButton(field.slug, 'BROKEN_LINKS')}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <label htmlFor={`missing-alt-text-rt-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Check Missing Alt Text</label>
                      <Tooltip text="Flags an error if any inline images inside the rich text are missing an alt attribute." />
                    </div>
                    <div className="flex items-center gap-1">
                      <input 
                        id={`missing-alt-text-rt-${field.slug}`}
                        type="checkbox" 
                        checked={rulesState[field.slug]?.['MISSING_ALT_TEXT']?.ruleValue === 'true'}
                        onChange={(e) => handleRuleChange(field.slug, 'MISSING_ALT_TEXT', e.target.checked)}
                        className="h-3.5 w-3.5 text-[var(--blueText)] rounded-[2px] bg-[var(--background3)] border-[var(--border2)]" 
                      />
                      {renderDeleteButton(field.slug, 'MISSING_ALT_TEXT')}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <label htmlFor={`max-image-size-rt-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Max File Size (Images)</label>
                      <Tooltip text="Flags an error if any inline image file size is larger than the selected limit." />
                    </div>
                    <div className="flex items-center gap-1">
                      <select 
                        id={`max-image-size-rt-${field.slug}`}
                        aria-label="Max File Size (Images)"
                        value={rulesState[field.slug]?.['MAX_IMAGE_SIZE']?.ruleValue || ''}
                        onChange={(e) => handleRuleChange(field.slug, 'MAX_IMAGE_SIZE', e.target.value)}
                        className="bg-[var(--background3)] border border-[var(--border2)] rounded-[2px] px-2 py-1 text-[11px] md:text-[12px] text-[var(--text1)]"
                      >
                        <option value="">No limit</option>
                        <option value="512000">500 KB</option>
                        <option value="1048576">1 MB</option>
                        <option value="2097152">2 MB</option>
                      </select>
                      {renderDeleteButton(field.slug, 'MAX_IMAGE_SIZE')}
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-1.5 w-full pt-1">
                    <div className="flex items-center">
                      <label htmlFor={`banned-words-rt-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Forbidden Words</label>
                      <Tooltip text="Flags an error if any of these comma-separated words are found inside the rich text." />
                    </div>
                    <div className="flex items-center gap-1 w-full">
                      <input 
                        id={`banned-words-rt-${field.slug}`}
                        type="text" 
                        placeholder="foo, bar"
                        value={rulesState[field.slug]?.['BANNED_WORDS']?.ruleValue || ''}
                        onChange={(e) => handleRuleChange(field.slug, 'BANNED_WORDS', e.target.value)}
                        className="bg-[var(--background3)] border border-[var(--border2)] rounded-[2px] px-2 py-1 w-full text-[11px] md:text-[12px] text-[var(--text1)]"
                      />
                      {renderDeleteButton(field.slug, 'BANNED_WORDS')}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center">
                      <label htmlFor={`trailing-spaces-rt-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Check Trailing Spaces</label>
                      <Tooltip text="Flags an error if the text begins or ends with whitespace." />
                    </div>
                    <div className="flex items-center gap-1">
                      <input 
                        id={`trailing-spaces-rt-${field.slug}`}
                        type="checkbox" 
                        checked={rulesState[field.slug]?.['TRAILING_SPACES']?.ruleValue === 'true'}
                        onChange={(e) => handleRuleChange(field.slug, 'TRAILING_SPACES', e.target.checked)}
                        className="h-3.5 w-3.5 text-[var(--blueText)] rounded-[2px] bg-[var(--background3)] border-[var(--border2)]" 
                      />
                      {renderDeleteButton(field.slug, 'TRAILING_SPACES')}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center">
                      <label htmlFor={`text-casing-rt-${field.slug}`} className="text-[11px] md:text-[12px] text-[var(--text2)]">Text Casing</label>
                      <Tooltip text="Flags an error if the text does not match the selected casing." />
                    </div>
                    <div className="flex items-center gap-1">
                      <select 
                        id={`text-casing-rt-${field.slug}`}
                        aria-label="Text Casing"
                        value={rulesState[field.slug]?.['TEXT_CASING']?.ruleValue || ''}
                        onChange={(e) => handleRuleChange(field.slug, 'TEXT_CASING', e.target.value)}
                        className="bg-[var(--background3)] border border-[var(--border2)] rounded-[2px] px-2 py-1 text-[11px] md:text-[12px] text-[var(--text1)]"
                      >
                        <option value="">Any</option>
                        <option value="LOWERCASE">lowercase</option>
                        <option value="UPPERCASE">UPPERCASE</option>
                      </select>
                      {renderDeleteButton(field.slug, 'TEXT_CASING')}
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return null;
        })}
        {fields.length === 0 && <p className="text-[11px] md:text-[12px] text-[var(--textInactive)]">No supported fields found.</p>}
      </div>
    </div>
  )
}
